import classNames from "classnames";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Accordion } from "../../../../../components/controls/accordion";
import GridSelectorInput from "../../../../../components/controls/GridSelectorInput";
import GridWrapper from "../../../../../components/controls/GridWrapper";
import { CurrencyInput, NumberInput, TextInput } from "../../../../../components/controls/inputs";
import BaseDocumentCreator from "../../../../../components/documents/baseDocumentCreator";
import { IEntitySimpleDTO } from "../../../../../libs/coreapi-dto/@types/common";
import { IPricingInvoiceItemViewDTO } from "../../../../../libs/coreapi-dto/dirs/pricingInvoice";
import { GoodsDataProvider } from "../../../../../Services/DataProviders/GoodsDataProvider";
import { PricingInvoiceDataProvider } from "../../../../../Services/DataProviders/PricingInvoiceDataProvider";
import { TaxTypeDataProvider } from "../../../../../Services/DataProviders/TaxTypeDataProvider";
import { useAppContext } from "../../../../../system/providers/appContextProvider";
import { useTabsContext } from "../../../../../system/providers/tabsProvider";
import { GoodsSelectorModal } from "../../../../Dictionaries/Goods";
import { ScalingRatioSelectorModal } from "../../../../Dictionaries/ScalingRatio";
import { SeriesModalWindow } from "../../../../Dictionaries/Series/SeriesAndCertificateModal/SeriesModalWindow";
import { StoreSelectorModal } from "../../../../Dictionaries/Store";
import { TaxTypeSelectorModal } from "../../../../Dictionaries/TaxType";
import collectPricingInvoiceItemDTO, { ICollectPricingInvoiceItemProps } from "../../CollectPricingInvoiceItemDTO";
import styles from "../../InvoiceItem/styles/InvoiceItemCreatorView.module.scss";
import { v4 as uuidv4, NIL as uuidNull } from "uuid";
import { IInvoiceItemCreateDTO } from "../../../../../libs/coreapi-dto/documents/invoiceItem";
import { ScalingRatioDataProvider } from "../../../../../Services/DataProviders/ScalingRatioDataProvider";
import { useForm } from "../../../../../system/hooks/useForm";
import { IDisplayItem, IGosContractFields } from "../InvoiceUpdateForm";
import { Checkbox } from "../../../../../components/checkboxes/checkbox";
import { CurrencyInputWithValidation, DecimalInputV2, ICurrencyInputValidation } from "../../../../../components/controls/inputs/BaseInput";
import { ISerialDTO } from "../../../../../libs/coreapi-dto/dirs/serial";
import { getKizCount } from "../../../../../system/functions/getKizCount";
import { MessageModalWindow } from "../../../../../components/modalWindows/MessageModalWindow";
import { ResendingTimer } from "../../../../../constants";
import useGridFilter from "../../../../../system/hooks/useGridFilter";
import { SerialDataProvider } from "../../../../../Services/DataProviders/SerialDataProvider";
import FlexRow from "../../../../../components/controls/FlexRow";
import { DateTime } from "luxon";
import RegisterPriceSelectorModal from "../../../../Dictionaries/RegisterPrice/RegisterPriceSelectorModal";
import { IContractorDTO } from "../../../../../libs/coreapi-dto/dirs/contractor";
import { ITaxTypeDTO } from "../../../../../libs/coreapi-dto/dirs/taxType";
import { CreateVariant } from "../../../../MasterTabsProvider";
import { ContractorDataProvider } from "../../../../../Services/DataProviders/ContractorDataProvider";
import { TaxType } from "../../../../../@types/enumsGlobal";
import { StoreDataProvider } from "../../../../../Services/DataProviders/StoreDataProvider";
import { TaxRatesByType } from "../../../ActRevaluation/ActRevaluationCreator/ActRevaluationForm";
import { IScalingRatioDTO } from "../../../../../libs/coreapi-dto/dirs/scalingRatio";
import { IGoodsDTO } from "../../../../../libs/coreapi-dto/dirs/goods";
import { getValueOrCallbackIfDiffer, onValueChanged } from "../../../../../system/functions/onValueChanged";
import { isEmpty } from "../../../../../system/functions/isEmpty";
import renderGlobalAlert from "../../../../../system/hooks/useGlobalAlert";
import { ConfirmationItemsModal } from "../../../../../components/modalWindows/ConfirmationItemsModal";
import { GosContractDataProvider } from "../../../../../Services/DataProviders/GosContractDataProvider";
import { useInvoiceItemTabSettings } from "./useInvoiceItemTabSettings";
import { useTabIndexManager } from "../../../../../system/hooks/useTabIndexManager";
import StorePlaceSelectorModal from "../../../../Dictionaries/StorePlace/StorePlaceSelectorModal";
import { StorePlaceDataProvider } from "../../../../../Services/DataProviders/StorePlaceDataProvider";

interface IValidator {
    idGoodsGlobal: string;
    idScalingRatioGlobal: string;
    quantity: number;
    producerPrice: number;
    registerPrice: number;
    supplierPrice: number;
    retailPrice: number;
    supplierVat: IEntitySimpleDTO;
    retailVat: IEntitySimpleDTO;
    supplierPriceWithVat: number;
    retailPriceWithVat: number;
    supplierSum: number;
    supplierVatSum: number;
    supplierSumWithVat: number;
    retailSum: number;
    retailSumWithVat: number;
    retailVatSum: number;
    actualQuantity: number;
}

interface IInvoiceItemCreateProps {
    idPricingModel: string;
    idSupplier: string;
    idStore: string;
    gosContractFields: IGosContractFields;
    goods: IEntitySimpleDTO;
    scalingRatio: IEntitySimpleDTO;
    parentCallback?: (entity: IInvoiceItemCreateDTO, displayItem: IDisplayItem) => void;
    item?: IInvoiceItemCreateDTO;
    displayItem: IDisplayItem;
    currentSupplier?: IContractorDTO;
    currentPharmacy?: IContractorDTO;
    defaultTax?: ITaxTypeDTO
    variant?: CreateVariant;
    isHardUnProcess?: boolean;
    documentStateProc?: boolean;
    documentDate?: DateTime;
    invoiceItemsGroup?: IInvoiceItemCreateDTO[];
    isGiftInvoice: boolean
}

interface ICalcSum {
    percentageOfMarkupRetail: number;
    supplierPriceWithVat: number;
    supplierMarkupPercentage: number;
    actualMarkupPercentage: number;
    supplierVatPerUnit: number;
    retailVatPerUnit: number;
}

interface ITaxRateFiller {
    tax: ITaxTypeDTO,
    useContratorRate: boolean
}

const InvoiceItemCreator: FC<IInvoiceItemCreateProps> = (props) => {
    const appContext = useAppContext();
    const tabsContext = useTabsContext();

    const gdp = new GoodsDataProvider(appContext.coreApiService);
    const scalingRatioDP = new ScalingRatioDataProvider(appContext.coreApiService);
    const pricingInvoiceDataProvider = new PricingInvoiceDataProvider(appContext.coreApiService);
    const taxTypeDataProvider = new TaxTypeDataProvider(appContext.coreApiService);
    const storePlaceDataProvider = new StorePlaceDataProvider(appContext.coreApiService);
    const sdp = new SerialDataProvider(appContext.coreApiService)
    const contractorDP = new ContractorDataProvider(appContext.coreApiService);
    const storeDP = new StoreDataProvider(appContext.coreApiService);
    const gosContractDP = new GosContractDataProvider(appContext.coreApiService);

    const defValue: IInvoiceItemCreateDTO = {
        quantity: 1,
        actualQuantity: 1,
        supplierCostInfo: { vat: 0, sum: 0, sumIncVat: 0, vatSum: 0, price: 0 },
        retailCostInfo: { vat: 0, sum: 0, sumIncVat: 0, vatSum: 0, price: 0 },
        registerPrice: 0,
    } as IInvoiceItemCreateDTO;

    const [data, setData] = useState(initializeValue);
    const priceContract = props.gosContractFields.gosContractGoods?.price ?? 0
    const [goods, setGoods] = useState<IEntitySimpleDTO | undefined>(props.goods);
    const [isLs, setIsLs] = useState<boolean>(false);
    const [storePlace, setStorePlace] = useState<IEntitySimpleDTO>()
    const [scalingRatio, setScalingRatio] = useState<IEntitySimpleDTO | undefined>(props.scalingRatio ?? undefined);
    const [vatRateWholesale, setVatRateWholesale] = useState<IEntitySimpleDTO | undefined>({} as IEntitySimpleDTO);
    const [retailVatRate, setRetailVatRate] = useState<IEntitySimpleDTO | undefined>({} as IEntitySimpleDTO);
    const [itemSeries, setItemSeries] = useState<ISerialDTO>({} as ISerialDTO);
    const [calcSum, setCalcSum] = useState<ICalcSum>({
        percentageOfMarkupRetail: props.item?.retailCostInfo?.adprice ?? props.displayItem?.productMargin ?? 0,
        supplierMarkupPercentage: 0,
        supplierPriceWithVat: 0,
        supplierVatPerUnit: 0,
        retailVatPerUnit: 0,
        actualMarkupPercentage: 0,
    });
    const [sendFetch, setSendFetch] = useState<boolean>(false);

    const requestPricingInvoiceTimeout = useRef<NodeJS.Timeout>();
    const shouldBlockRecalculation = useRef<boolean>(false);
    const [isKiz, setIsKiz] = useState<boolean>(false);
    const [isGnvls, setIsGnvls] = useState<boolean>(false);
    const validateReestr = { isGnvls, documentDate: props.documentDate };
    const [isOpenSeriesModal, setIsOpenSeriesModal] = useState<boolean>(false);
    const [pricingInvoice, setPricingInvoice] = useState<Partial<IPricingInvoiceItemViewDTO | undefined>>(() => {
        if (props.variant === 'create') {
            return undefined
        }
        return {
            actualMarkupPercentage: props.item?.productMargin ?? 0,
            percentageOfMarkupRetail: props.item?.retailCostInfo?.adprice ?? props.displayItem?.productMargin ?? 0
        }
    });

    const [itemsGroupAlert, setItemsGroupAlert] = useState<{ show: boolean, tabToClose?: ITab }>({ show: false });
    const [itemsGroupAlertFlag, setItemsGroupAlertFlag] = useState<boolean>(false);

    const [modalWindow, setModalWindow] = useState(<></>);

    const [taxTypeGridFilter] = useGridFilter();

    const collectionData: ICollectPricingInvoiceItemProps = {
        eventTypes: "None",
        idInvoice: "",
        idSupplier: props.idSupplier,
        idStore: props.idStore,
        idItem: data?.idGlobal,
        idGoods: goods?.idGlobal as string,
        idScalingRatio: scalingRatio?.idGlobal,
        quantity: data?.quantity,
        actualQuantity: data?.actualQuantity,
        damageQuantity: data?.damageQuantity,
        defectQuantity: data?.defectQuantity,
        shortageQuantity: data?.shortageQuantity,
        producerPrice: data.producerPrice,
        retailCostInfo: data?.retailCostInfo,
        supplierCostInfo: data?.supplierCostInfo,
        registerPrice: data?.registerPrice,
    }
    const containerRef = useRef<HTMLDivElement>(null);
    const { getTabIndex, getBooleanSetting } = useInvoiceItemTabSettings();
    useTabIndexManager('invoiceItemCreator', containerRef);
    
    const [isWeight, setIsWeight] = useState<boolean>(false);
    const { t } = useTranslation();
    const baseT = (value: string) => t("documents.invoice." + value);
    const errorsT = (value: string) => t("errors." + value);

    const disabledGosContract = props.item?.idGosContractGoodsExternalGlobal && getBooleanSetting('disabledFieldForGC')
    
    const { isValid, errors, setErrors } = useForm<IValidator>({
        validations: {
            idGoodsGlobal: {
                required: {
                    value: goods ? false : true,
                    message: errorsT("required"),
                },
            },
            idScalingRatioGlobal: {
                required: {
                    value: data.idScalingRatioGlobal ? false : true,
                    message: errorsT("required"),
                },
            },
            quantity: {
                required: {
                    value: data.quantity && data?.quantity > 0 ? false : true,
                    message: errorsT("onlyPositiveValue"),
                },
            },
            supplierVat: {
                required: {
                    value: vatRateWholesale ? false : true,
                    message: errorsT("required"),
                },
            },
            retailVat: {
                required: {
                    value: retailVatRate ? false : true,
                    message: errorsT("required"),
                },
            },
            producerPrice: {
                custom: {
                    isValid: () => {
                        return isValidProducerPrice().bool;
                    },
                    message: errorsT(isValidProducerPrice().message),
                },
            },
            registerPrice: {
                required: {
                    value: isGnvls && !data.registerPrice ? true : false,
                    message: errorsT("required"),
                },
            },
            supplierPrice: {
                required: {
                    value: props.isGiftInvoice || data.supplierCostInfo.price > 0 ? false : true,
                    message: errorsT("required"),
                },
            },
            retailPrice: {
                required: {
                    value: props.isGiftInvoice || data.retailCostInfo.price > 0 ? false : true,
                    message: errorsT("required"),
                },
            },
            supplierPriceWithVat: {
                required: {
                    value: props.isGiftInvoice || data.supplierCostInfo.priceIncVat > 0 ? false : true,
                    message: errorsT("required"),
                },
            },
            retailPriceWithVat: {
                custom: {
                    isValid() {
                        if (data.retailCostInfo.price <= 0 && !props.isGiftInvoice) return false
                        if (props.gosContractFields.referentPrice && data.retailCostInfo.priceIncVat > priceContract) return false
                        return true
                    },
                    message: data.retailCostInfo.price <= 0 ?  errorsT("required") :  errorsT("gosContractSellingOutOfRange")
                }
            },
            retailSum: {
                required: {
                    value: props.isGiftInvoice || data.retailCostInfo.sum > 0 ? false : true,
                    message: errorsT("required"),
                },
            },
            supplierSum: {
                required: {
                    value: props.isGiftInvoice || data.supplierCostInfo.sum > 0 ? false : true,
                    message: errorsT("required"),
                },
            },
            retailVatSum: {
                required: {
                    value: data.retailCostInfo.vatSum >= 0 ? false : true,
                    message: errorsT("required"),
                },
            },
            supplierVatSum: {
                required: {
                    value: data.supplierCostInfo.vatSum >= 0 ? false : true,
                    message: errorsT("required"),
                },
            },
            retailSumWithVat: {
                required: {
                    value: props.isGiftInvoice || data.retailCostInfo.sumIncVat > 0 ? false : true,
                    message: errorsT("required"),
                },
            },
            supplierSumWithVat: {
                required: {
                    value: props.isGiftInvoice || data.supplierCostInfo.sumIncVat > 0 ? false : true,
                    message: errorsT("required"),
                },
            },
            actualQuantity: {
                required: {
                    value: data?.actualQuantity as number < 0 ? true : false,
                    message: errorsT("onlyNotNegativeValue"),
                },
            }
        },
    });

    function isValidProducerPrice() {
        if (!isGnvls) return { bool: true, message: "" };
        if (props.isGiftInvoice) return { bool: true, message: "" };
        if (data.producerPrice === undefined || data.producerPrice <= 0) return { bool: false, message: "required" };
        if (data.producerPrice > data.registerPrice) return { bool: false, message: "exceededProducerPrice" };
        return { bool: true, message: "" };
    }

    function initializeValue(): IInvoiceItemCreateDTO {
        if (props.item) {
            return { ...props.item };
        }

        if (props.gosContractFields.isGosContract && props.gosContractFields.gosContractGoods) {
            return {
                ...defValue,
                retailCostInfo: {
                    ...defValue.retailCostInfo,
                    priceIncVat: props.gosContractFields.gosContractGoods.price,
                },
            } as IInvoiceItemCreateDTO;
        } else {
            return { ...defValue };
        }
    }

    useEffect(() => {
        if (props.item?.idSeriesGlobal) {
            sdp.getById(props.item?.idSeriesGlobal, (e) => setItemSeries(e))
        }
        if (props?.invoiceItemsGroup) {
            for (const char of props?.invoiceItemsGroup as IInvoiceItemCreateDTO[]) {
                if (char.kizBoxes?.length as number > 1) {
                    setItemsGroupAlertFlag(true);
                    break;
                } else {
                    setItemsGroupAlertFlag(false);
                }
            }
        }

        if (props.variant === 'edit' && props.item) {
            if (props.item.idStorePlaceGlobal) {
                storePlaceDataProvider.getById(props.item.idStorePlaceGlobal, (response) => {
                    setStorePlace({displayName: response.name, idGlobal: response.idGlobal})
                })
            }

            const hasKizs = props.item.kizs && props.item.kizs.length > 0;
            const hasDamageQuantity = props.item.damageQuantity !== undefined && props.item.damageQuantity !== null && props.item.damageQuantity > 0;
            const hasDefectQuantity = props.item.defectQuantity !== undefined && props.item.defectQuantity !== null && props.item.defectQuantity > 0;
            const hasShortageQuantity = props.item.shortageQuantity !== undefined && props.item.shortageQuantity !== null && props.item.shortageQuantity > 0;
            const hasAnyQuantityField = hasDamageQuantity || hasDefectQuantity || hasShortageQuantity;
            if (hasKizs && hasAnyQuantityField) {
                shouldBlockRecalculation.current = true;
            }
        }
    }, [])

    useEffect(() => {
        if (shouldBlockRecalculation.current) {
            setCalcSum(prev => ({
                ...prev,
                actualMarkupPercentage: pricingInvoice?.actualMarkupPercentage ?? prev.actualMarkupPercentage,
            }));
            return;
        }
        
        if (props.variant === 'edit' && pricingInvoice && 
            !pricingInvoice.retailPrice && !pricingInvoice.supplierPriceExcludingVat) {
            setCalcSum({
                ...calcSum,
                actualMarkupPercentage: pricingInvoice?.actualMarkupPercentage ?? 0,
            });
            return;
        }

        setCalcSum({
            ...calcSum,
            actualMarkupPercentage: pricingInvoice?.actualMarkupPercentage ?? 0,
            supplierVatPerUnit: pricingInvoice?.supplierVatPerUnit ?? 0,
            retailVatPerUnit: pricingInvoice?.retailVatPerUnit ?? 0,
            percentageOfMarkupRetail: pricingInvoice?.percentageOfMarkupRetail ?? 0, //"Наценка %" ? (процент розничной наценки)
            supplierMarkupPercentage: pricingInvoice?.supplierMarkupPercentage ?? 0, //Опт. надбавка, %
            supplierPriceWithVat: pricingInvoice?.supplierPriceWithVat ?? 0, //Цена поставщика с НДС
        });
        setData({
            ...data,
            quantity: data?.quantity,
            idGosContractExternalGlobal: data?.idGosContractExternalGlobal,
            idGosContractGoodsExternalGlobal: data?.idGosContractGoodsExternalGlobal,
            retailCostInfo: {
                ...data?.retailCostInfo,
                price: pricingInvoice?.retailPrice ?? 0, //Цена отпускная без НДС
                vatPrice: pricingInvoice?.retailVatPerUnit ?? 0,
                priceIncVat: props.gosContractFields.gosContractGoods ?
                    data.retailCostInfo.priceIncVat :
                    pricingInvoice?.retailPriceWithVat ?? 0, //Цена отпускная с НДС
                sum: pricingInvoice?.retailAmount ?? 0, //Сумма розничная без НДС
                vatSum: pricingInvoice?.vatAmountRetail ?? 0, //Сумма НДС розничная
                sumIncVat: pricingInvoice?.retailAmountWithVat ?? 0, //Сумма розничная с НДС
                adprice: pricingInvoice?.percentageOfMarkupRetail ?? 0,
            },
            supplierCostInfo: {
                ...data?.supplierCostInfo,
                price: pricingInvoice?.supplierPriceExcludingVat ?? 0, //Цена поставщика без НДС
                vatPrice: pricingInvoice?.supplierVatPerUnit ?? 0,
                priceIncVat: pricingInvoice?.supplierPriceWithVat ?? 0, //Цена поставщика с НДС
                sum: pricingInvoice?.supplierAmountExcludingVat ?? 0, //Сумма поставщика без НДС
                vatSum: pricingInvoice?.supplierVatAmount ?? 0, //Сумма НДС поставщика
                sumIncVat: pricingInvoice?.supplierAmountWithVat ?? 0, //Сумма поставщика с НДС
                adprice: pricingInvoice?.supplierMarkupPercentage,
            },
            producerPrice: pricingInvoice?.manufacturerPrice ?? 0,
        });
    }, [pricingInvoice]);

    const getCalculatedInvoiceItem = (collectionProps: ICollectPricingInvoiceItemProps, forceUnlock: boolean = true) => {
        if (forceUnlock) {
            shouldBlockRecalculation.current = false;
        }

        if (shouldBlockRecalculation.current) {
            return;
        }

        if (goods !== undefined && props.idPricingModel) {
            setSendFetch(true);
            let pricingInvoiceDTO = collectPricingInvoiceItemDTO(collectionProps);
            clearTimeout(requestPricingInvoiceTimeout.current as NodeJS.Timeout);
            requestPricingInvoiceTimeout.current = setTimeout(() => {
                const alertSettings = {
                    title: errorsT('notCorrectRetailPrice'),
                    filterError: () => {
                        return data.supplierCostInfo.sumIncVat === 0;
                    }
                };
                pricingInvoiceDataProvider.getCalculatedInvoiceItemViewAsync(props.idPricingModel, pricingInvoiceDTO, (entity) => {
                    setPricingInvoice((prev) => {
                        let newPricingInvoice: IPricingInvoiceItemViewDTO;
                        if (prev && prev.eventType === entity.eventType) {
                            newPricingInvoice = {
                                ...prev,
                                ...entity,
                            };
                        } else {
                            newPricingInvoice = entity;
                        }
                        if (props.variant === 'create') {
                            updateTaxRateFromDoc(newPricingInvoice);
                        }
                        return newPricingInvoice;
                    });
                    setSendFetch(false);
                }, alertSettings);
            }, ResendingTimer);
        }
    };

    const getCalculatedInvoiceItemEndChange = (collectionProps: ICollectPricingInvoiceItemProps, forceUnlock: boolean = true) => {
        if (forceUnlock) {
            shouldBlockRecalculation.current = false;
        }

        if (shouldBlockRecalculation.current) {
            return;
        }

        if (goods !== undefined && props.idPricingModel) {
            setSendFetch(true);
            let pricingInvoiceDTO = collectPricingInvoiceItemDTO(collectionProps);
            clearTimeout(requestPricingInvoiceTimeout.current as NodeJS.Timeout);
            pricingInvoiceDataProvider.getCalculatedInvoiceItemViewAsync(props.idPricingModel, pricingInvoiceDTO, (entity) => {
                setPricingInvoice((prev) => {
                    if (prev && prev.eventType === entity.eventType) {
                        return {
                            ...prev,
                            ...entity,
                        };
                    } else {
                        return entity;
                    }
                });
                setSendFetch(false);
            });
        }
    };

    const calcBySupplierVatRatePercentageChanged = (newVatValue: number) => {
        getCalculatedInvoiceItem({
            ...collectionData,
            eventTypes: 'SupplierVatRatePercentageChanged',
            supplierCostInfo: { ...data?.supplierCostInfo, vat: newVatValue },
        })
    };

    const calcByPercentageOfVatRateRetailChanged = (newVatValue: number) => {
        getCalculatedInvoiceItem({
            ...collectionData,
            eventTypes: 'PercentageOfVatRateRetailChanged',
            retailCostInfo: { ...data?.retailCostInfo, vat: newVatValue },
        })
    };

    const calcActualQuantity = (value: number, typeValue: string) => {
        switch (typeValue) {
            case 'quantity':
                return setData({ ...data, quantity: value, actualQuantity: (value - (data.damageQuantity ?? 0) - (data.defectQuantity ?? 0) - (data.shortageQuantity ?? 0)) });
            case 'damageQuantity':
                return setData({ ...data, damageQuantity: value, actualQuantity: (data.quantity - value - (data.defectQuantity ?? 0) - (data.shortageQuantity ?? 0)) });
            case 'defectQuantity':
                return setData({ ...data, defectQuantity: value, actualQuantity: (data.quantity - (data.damageQuantity ?? 0) - value - (data.shortageQuantity ?? 0)) });
            case 'shortageQuantity':
                return setData({ ...data, shortageQuantity: value, actualQuantity: (data.quantity - (data.damageQuantity ?? 0) - (data.defectQuantity ?? 0) - value) });
        }
    }

    const getCerificateId = (): string | undefined => {
        if (!itemSeries) return undefined;
        if (!itemSeries.certificates) return undefined;
        return itemSeries.certificates[0]?.idGlobal;
    };

    function updateTaxRateFromDoc(currentPricingInvoice?: any)
    {
        taxTypeDataProvider.getView(taxTypeGridFilter, (dat) => {
            const pricingInvoiceToUse = currentPricingInvoice || pricingInvoice;
            if (props.item?.supplierCostInfo.vat)
            {
                let supVat = dat.find(x=>x.taxRate === props.item?.supplierCostInfo.vat);
                if (supVat)
                {
                    setVatRateWholesale({idGlobal: supVat.idGlobal, displayName: supVat.displayName});
                    setData(prevData => ({ 
                        ...prevData, 
                        supplierCostInfo: { 
                            ...prevData?.supplierCostInfo, 
                            vat: supVat?.taxRate 
                        } 
                    }));
                    calcBySupplierVatRatePercentageChanged(supVat.taxRate);
                }
            }
            if (props.item?.retailCostInfo.vat)
            {
                let retVat = dat.find(x=>x.taxRate === props.item?.retailCostInfo.vat);
                if (retVat)
                {
                    setRetailVatRate({idGlobal: retVat.idGlobal, displayName: retVat.displayName});
                    setData(prevData => ({ 
                        ...prevData, 
                        retailCostInfo: { 
                            ...prevData?.retailCostInfo, 
                            vat: retVat?.taxRate 
                        } 
                    }));
                    calcByPercentageOfVatRateRetailChanged(retVat.taxRate);
                }
            }
            if (pricingInvoiceToUse && props.variant === 'create') {
                let unDeletetRetVat = dat.filter(x => x.deleted !== true)
                let retVat = unDeletetRetVat.find(x=>x.taxRate === pricingInvoiceToUse.percentageOfVatRateRetail);
                if (retVat)
                {
                    setRetailVatRate({idGlobal: retVat.idGlobal, displayName: retVat.displayName});
                    setData(prevData => ({ 
                        ...prevData, 
                        retailCostInfo: { 
                            ...prevData?.retailCostInfo, 
                            vat: retVat?.taxRate 
                        } 
                    }));
                }
            }
        })
    }

    function setTaxRates(taxTypeRetail: ITaxTypeDTO, taxTypeSupplier: ITaxTypeDTO, registerPrice: number) {
        registerPrice = props.item?.registerPrice ?? registerPrice
        setRetailVatRate(taxTypeRetail);
        setVatRateWholesale(taxTypeSupplier);
        setData({
            ...data,
            supplierCostInfo: { ...data.supplierCostInfo, vat: taxTypeSupplier.taxRate },
            retailCostInfo: { ...data.retailCostInfo, vat: taxTypeRetail.taxRate },
            registerPrice: (registerPrice ?? undefined),
            idScalingRatioGlobal: scalingRatio?.idGlobal ?? undefined,
        });
        
    }

    function updateTaxRates(goodsTaxType, goodsTaxTypeSupplier, taxType, taxTypeSupplier, registerPrice, idScalingRatioGlobal)
    {   
      if (goodsTaxType && goodsTaxTypeSupplier)
        {
            // setRetailVatRate(goodsTaxType);
            setVatRateWholesale(goodsTaxTypeSupplier);
            setData({
                ...data,
                supplierCostInfo: { ...data.supplierCostInfo, vat: taxTypeSupplier.taxRate },
                retailCostInfo: { ...data.retailCostInfo, vat: taxType.taxRate },
                registerPrice: (registerPrice ?? undefined),
                idScalingRatioGlobal: idScalingRatioGlobal ?? undefined,
            });
        } else if (goodsTaxType) {
            // setRetailVatRate(goodsTaxType);
            setVatRateWholesale(taxTypeSupplier != undefined ? taxTypeSupplier : goodsTaxType);
            setData({
                ...data,
                supplierCostInfo: { ...data.supplierCostInfo, vat: taxType.taxRate },
                retailCostInfo: { ...data.retailCostInfo, vat: taxType.taxRate },
                registerPrice: (registerPrice ?? undefined),
                idScalingRatioGlobal: idScalingRatioGlobal ?? undefined,
            });
        } else if (goodsTaxTypeSupplier) {
            // setRetailVatRate(goodsTaxType);
            setVatRateWholesale(goodsTaxTypeSupplier);
            setData({
                ...data,
                supplierCostInfo: { ...data.supplierCostInfo, vat: taxTypeSupplier.taxRate },
                retailCostInfo: { ...data.retailCostInfo, vat: taxTypeSupplier.taxRate },
                registerPrice: (registerPrice ?? undefined),
                idScalingRatioGlobal: idScalingRatioGlobal ?? undefined,
            });
        }

        if (props.item?.supplierCostInfo.vat || props.item?.retailCostInfo.vat)
            updateTaxRateFromDoc();
    }

    useEffect(() => {
        if (goods?.idGlobal) {
            gdp.getByIdAndScalingRatio(goods.idGlobal as string, scalingRatio?.idGlobal ?? uuidNull, (goodsDto: IGoodsDTO) => {

                setIsLs(goodsDto.isLs)
                setIsWeight(goodsDto.attributes.isWeight ?? false);
                
                if (goodsDto.isLs !== true) {
                    shouldBlockRecalculation.current = false;
                }

                if (props.variant !== 'edit') {
                    setStorePlace(goodsDto.storePlace);
                }

                scalingRatioDP.overrideGetById(goods.idGlobal, goodsDto.scalingRatio.idGlobal, (scalingRatioDto: IScalingRatioDTO) => {
                    setScalingRatio({
                        displayName: scalingRatioDto.displayName,
                        idGlobal: scalingRatioDto.idGlobal
                    });
                    setData(prev => ({
                        ...prev,
                        idScalingRatioGlobal: scalingRatioDto.idGlobal,
                        numerator: scalingRatioDto.numerator,
                        denominator: scalingRatioDto.denominator
                    }));
                });

                setIsLs(goodsDto.isLs)
                if (props.variant === 'edit') {
                    taxTypeDataProvider.getByRate(data.retailCostInfo.vat, (taxType) => {
                        taxTypeDataProvider.getByRate(data.supplierCostInfo.vat, (taxTypeSupplier) => {
                            setTaxRates(taxType, taxTypeSupplier, goodsDto.gnvls.registerPrice as number)
                            setIsKiz((props.item?.isKiz || props.item?.isKizBox) ?? goodsDto.attributes.isKiz ?? false);
                            setIsGnvls(goodsDto.gnvls.important ?? false);
                        });
                    });
                }
                else if (goodsDto?.taxType && goodsDto?.taxTypeSupplier) {
                    taxTypeDataProvider.getById(goodsDto.taxType.idGlobal, (taxType) => {
                        taxTypeDataProvider.getById(goodsDto.taxTypeSupplier.idGlobal, (taxTypeSupplier) => {
                            updateTaxRates(goodsDto.taxType, goodsDto.taxTypeSupplier, taxType, taxTypeSupplier, goodsDto.gnvls.registerPrice, goodsDto?.scalingRatio.idGlobal);
                            setIsKiz((props.item?.isKiz || props.item?.isKizBox) ?? goodsDto.attributes.isKiz ?? false);
                            setIsGnvls(goodsDto.gnvls.important ?? false);
                        });
                    });
                } else if (goodsDto?.taxType) {

                    const upd = (wholeSale: ITaxRateFiller, supplier: ITaxRateFiller) => {
                        updateTaxRates(wholeSale.useContratorRate ? wholeSale.tax : goodsDto.taxType, 
                            goodsDto.taxTypeSupplier, 
                            wholeSale.tax,
                            supplier.useContratorRate ? supplier.tax : undefined, 
                            goodsDto.gnvls.registerPrice, 
                            goodsDto?.scalingRatio.idGlobal);
                        setIsKiz((props.item?.isKiz || props.item?.isKizBox) ?? goodsDto.attributes.isKiz ?? false);
                        setIsGnvls(goodsDto.gnvls.important ?? false);
                    };

                    storeDP.getById(props.idStore, (store) => {
                        contractorDP.getById(store.contractor.idGlobal, (contractor: IContractorDTO) => {
                            contractorDP.getById(props.idSupplier, (supplier) => {

                                const taxRateSupplier: number = supplier.useVat && (supplier.taxType == TaxType.SimplifiedRate5 || supplier.taxType == TaxType.SimplifiedRate7) ?
                                    (supplier.taxType == TaxType.SimplifiedRate5 ? 5 : 7) : TaxRatesByType[goodsDto.taxType.displayName];

                                if (taxRateSupplier !== 0 && contractor.useVat &&
                                    (contractor.taxType == TaxType.SimplifiedRate5 || contractor.taxType == TaxType.SimplifiedRate7)) {

                                    let wholeSaleRate = contractor.taxType == TaxType.SimplifiedRate5 ? 5 : 7;
                                    taxTypeDataProvider.getByRate(wholeSaleRate, (taxTypeWholesale) => {
                                        taxTypeDataProvider.getByRate(taxRateSupplier, (taxTypeSupplier) => {
                                            upd({ tax: taxTypeWholesale, useContratorRate: true } as ITaxRateFiller, 
                                                { tax: taxTypeSupplier, useContratorRate: true } as ITaxRateFiller);
                                        })
                                    });
                                } else {
                                    taxTypeDataProvider.getById(goodsDto.taxType.idGlobal, (taxTypeWholesale) => {
                                        taxTypeDataProvider.getByRate(taxRateSupplier, (taxTypeSupplier) => {
                                            upd({ tax: taxTypeWholesale, useContratorRate: true } as ITaxRateFiller, 
                                                { tax: taxTypeSupplier, useContratorRate: true } as ITaxRateFiller);
                                        })
                                    });
                                }
                            });
                        });
                    });
                } else if (goodsDto?.taxTypeSupplier) {
                    taxTypeDataProvider.getById(goodsDto.taxTypeSupplier.idGlobal, (taxTypeSupplier) => {
                        updateTaxRates(goodsDto.taxType, goodsDto.taxTypeSupplier, undefined, taxTypeSupplier, goodsDto.gnvls.registerPrice, goodsDto?.scalingRatio.idGlobal );
                        setIsKiz((props.item?.isKiz || props.item?.isKizBox) ?? goodsDto.attributes.isKiz ?? false);
                        setIsGnvls(goodsDto.gnvls.important ?? false);
                    });
                }
            });
        } else {
            setData({ ...data, registerPrice: 0, idScalingRatioGlobal: undefined, idGoodsGlobal: "" });
            setIsKiz((props.item?.isKiz || props.item?.isKizBox) ?? false);
            setScalingRatio(undefined);
            setIsGnvls(false);
        }
    }, [goods]);

    const confirmScalingRatioChange = (value: IEntitySimpleDTO, changeScalingRatio: (newData?: IInvoiceItemCreateDTO) => void) => {
        const numerator: number = parseInt(value.displayName.split('/')[0], 10);

        // Если выбираем ед. измерения короба, но отсканированы КИЗ для строки
        // уведомление
        if (numerator > 1 && data.kizs && data.kizs.length > 0) {
            setModalWindow(
                <ConfirmationItemsModal
                    header={`${t("errors.warning")}`}
                    warning={`Внимание! Если вы измените ед. изм. на "${value.displayName}", то отсканированные КИЗ будут очищены.`}
                    listPositions={`${data.kizs.map(x => ` ● GTIN-SGTIN: ${x.gtinSGtin}`).join('\n')}`}
                    question={`Строка автоматически переведётся в режим короба.\nПодвердить действие?`}
                    cancel={() => {
                        setModalWindow(<></>);
                    }}
                    ok={() => {
                        setData((prevData) => {
                            const newData = { ...prevData, kizs: [], kizCount: 0 };
                            changeScalingRatio?.(newData);
                            return newData;
                        });
                        
                        setModalWindow(<></>);
                    }}
                />
            );
        } else {
            changeScalingRatio?.();
        }
    }

    useEffect(() => {
        if (scalingRatio) {
            const numerator: number = parseInt(scalingRatio.displayName.split('/')[0], 10);
            let targetQuantity: number = 0;
            let scanText: string = "";
            
            // Если уже сканировали короба + изменили ед измерения, то выставить количество принудительно
            if (data.kizBoxes && data.kizBoxes.length > 0) {

                targetQuantity += data.kizBoxes.length;
                if (numerator === 1)
                    targetQuantity = data.kizBoxes.reduce((sum, box) => sum + box.quantity, 0);

                scanText += "SSCC"
            }

            // Россыпь или КИЗ
            if (data.kizs && data.kizs.length > 0) {

                if (numerator === 1) {
                    targetQuantity += data.kizs.length;
                    scanText += scanText.length > 0 ? " и КИЗ" : "КИЗ";
                }           
            }

            if (targetQuantity > 0) {
                setData({ ...data, quantity: targetQuantity });
                calcActualQuantity(targetQuantity, 'quantity');
                
                // Передаем forceUnlock = false, чтобы не снимать блокировку при автоматическом вызове
                getCalculatedInvoiceItem({
                    ...collectionData,
                    eventTypes: "QuantityСhanged",
                    quantity: targetQuantity,
                }, false);

                renderGlobalAlert({
                    variant: "info",
                    title: `В строке документа отсканированы ${scanText}, сум. кол-во товара: ${targetQuantity}`,
                    statusCode: 0,
                    durationMsec: 4000
                });
            }
        }
    }, [scalingRatio])

    const checkExecutionAmountGosContractGoods = (goods: IEntitySimpleDTO): Promise<void> => {
        return new Promise((resolve) => {  

            const idGosContractGlobal = props.gosContractFields.idGosContractGlobal;
            const idGlobal = props.gosContractFields.gosContractGoods?.idGlobal;
                
            if (!idGosContractGlobal || idGlobal == null) {
                resolve();
                return;
            }

            // Добавляем таймаут на случай если callback никогда не вызовется
            const timeoutId = setTimeout(() => {
                resolve();
            }, 1000); // 1 секунд таймаут

            gosContractDP.getItemView(props.gosContractFields.idGosContractGlobal as string, props.gosContractFields.gosContractGoods?.idGlobal, (gosContractGoods) => {
                clearTimeout(timeoutId);
                if (gosContractGoods == null) {
                    resolve();
                    return;
                }

                try {
                    const executionAmount = gosContractGoods.executionAmount;
                    const executionAmountFinal = gosContractGoods?.price * gosContractGoods.qty;
                    const executionAmountNew = executionAmount + (data.retailCostInfo.priceIncVat * data.quantity);

                    if (executionAmountNew > executionAmountFinal) {
                        renderGlobalAlert({ 
                            variant: "warning", 
                            statusCode: 500, 
                            title: `${baseT(`messages.executionAmountWarning`)} '${goods.displayName}'`, 
                            detail: `Сумма выполнения контракта по товару '${goods.displayName}' = ${executionAmountFinal}, после добавление позиции сумма будет = ${executionAmountNew}`
                        });
                    }
                    resolve();
                } catch (error) {
                    console.error('Error in checkExecutionAmountGosContractGoods:', error);
                    resolve(); // Все равно разрешаем Promise, чтобы не блокировать
                }
            });
        });
    }

    const saveInvoiceItem = async () => {
      

        if (goods) {
            try {
                await checkExecutionAmountGosContractGoods(goods);
            } catch (error) {
                console.error('Check execution amount failed:', error);
                // Продолжаем выполнение несмотря на ошибку
            }

            gdp.getById(goods.idGlobal, (goodsDto: IGoodsDTO) => {
                let idGlobal: string = uuidv4();

                const idScalingRatioGlobal: string | undefined = getValueOrCallbackIfDiffer(
                    [
                        scalingRatio?.idGlobal,
                        data.idScalingRatioGlobal,
                        goodsDto.scalingRatio.idGlobal
                    ],
                    () => {
                        return scalingRatio ? scalingRatio.idGlobal : data.idScalingRatioGlobal ?? goodsDto.scalingRatio.idGlobal;
                    })

                props.parentCallback?.(
                    {
                        idGlobal: data.idGlobal ?? idGlobal,
                        idGoodsGlobal: goods.idGlobal,
                        idScalingRatioGlobal: idScalingRatioGlobal,
                        idSeriesGlobal: isEmpty(itemSeries) ? undefined : itemSeries.idGlobal,
                        IdCertificateExternalGlobal: getCerificateId(),
                        idInvoiceItemGroupGlobal: data.idInvoiceItemGroupGlobal,
                        idGosContractExternalGlobal: data.idGosContractExternalGlobal,
                        idGosContractGoodsExternalGlobal: props.gosContractFields.isGosContract ? props.gosContractFields.gosContractGoods?.idGlobal : data.idGosContractGoodsExternalGlobal,
                        quantity: data.quantity,
                        producerPrice: data.producerPrice,
                        productMargin: calcSum.actualMarkupPercentage,
                        supplierCostInfo: data.supplierCostInfo,
                        retailCostInfo: data.retailCostInfo,
                        supplierGoodsCode: data.supplierGoodsCode,
                        gtdNumber: data.gtdNumber,
                        barCode: data.barCode,
                        gtin: data.gtin,
                        registerPrice: data.registerPrice,
                        isKiz: isKiz,
                        isKizBox: data.kizBoxes ? data.kizBoxes.length > 0 : false,
                        isLs: isLs,
                        goodsCode: data.goodsCode,
                        producer: data.producer,
                        kizs: data.kizs ?? [],
                        kizOsuDtos: data.kizOsuDtos ?? [],
                        kizBoxes: data.kizBoxes ?? [],
                        kizBoxesGisMt: data.kizBoxesGisMt ?? [],
                        kizBoxCount: data.kizBoxCount,
                        kizCount: data.kizBoxCount,
                        denominator: data.denominator,
                        numerator: data.numerator,
                        dateCreated: DateTime.now(),
                        isOsu: data.isOsu,
                        kizOsuDto: data.kizOsuDto,
                        actualQuantity: data.actualQuantity,
                        damageQuantity: data.damageQuantity,
                        defectQuantity: data.defectQuantity,
                        shortageQuantity: data.shortageQuantity,
                        isPieceAccountingKizOsu: data.isPieceAccountingKizOsu,
                        idStorePlaceGlobal: storePlace?.idGlobal
                    },
                    {
                        idGlobal: data.idGlobal ?? idGlobal,
                        idGoodsGlobal: goods.idGlobal,
                        absentInGC: props.gosContractFields.isGosContract ? (props.gosContractFields.gosContractGoods ? false : true) : null,
                        goodsName: goods.displayName,
                        scalingRatioName: scalingRatio?.displayName as string,
                        quantity: data.quantity,
                        productMargin: calcSum.percentageOfMarkupRetail,
                        producerPrice: data.producerPrice,
                        retailPrice: data.retailCostInfo.price as number,
                        retailPriceIncVat: data.retailCostInfo.priceIncVat as number,
                        retailAmount: data.retailCostInfo.sum,
                        retailVat: data.retailCostInfo.vat,
                        retailAmountIncVat: data.retailCostInfo.sumIncVat,
                        supplierAmount: data.supplierCostInfo.sum,
                        supplierAmountIncVat: data.supplierCostInfo.sumIncVat,
                        supplierPrice: data.supplierCostInfo.price,
                        supplierPriceIncVat: data.supplierCostInfo.priceIncVat,
                        supplierVat: data.supplierCostInfo.vat,
                        gtin: data.gtin,
                        isKiz: isKiz,
                        goodsCode: goodsDto.code,
                        producer: goodsDto.producer.displayName,
                        isGnvls: isGnvls,
                        countKizScan: isKiz === false ? undefined : props.displayItem?.countKizScan ?? 0,
                        idInvoiceItemGroupGlobal: data.idInvoiceItemGroupGlobal,
                        denominator: data.denominator,
                        numerator: data.numerator,
                        dateCreated: DateTime.now(),
                        bestBefore: itemSeries?.bestBefore,
                        seriesNumber: itemSeries?.seriesNumber,
                        isOsu: data.isOsu,
                        actualQuantity: data.actualQuantity,
                        damageQuantity: data.damageQuantity,
                        defectQuantity: data.defectQuantity,
                        shortageQuantity: data.shortageQuantity,
                        storePlaceName: storePlace?.displayName
                    }
                );
            })
        }
    };

    const getCurrencyInputValidation = (): ICurrencyInputValidation[] => {
        const result = [] as ICurrencyInputValidation[];
        if (props.gosContractFields.isGosContract && props.gosContractFields.gosContractGoods) {
            result.push({
                warning: {
                    maxValue: {
                        max: props.gosContractFields.gosContractGoods.qty,
                        name: baseT("gosContractOutOfRange"),
                    },
                },
            } as ICurrencyInputValidation);
        }
        return result;
    };

    return (
        <div ref={containerRef}>
            <BaseDocumentCreator
                valid={true}
                cancel={{
                    onClick: () => tabsContext.closeCurrent(),
                    tabIndex: getTabIndex('cancelButton'),
                    'data-field-id': 'cancelButton'
                }}
                ok={{
                    onClick: async () => {
                        const tabToClose = tabsContext.currentTab;
                        if (isValid() && !sendFetch) {
                            await saveInvoiceItem();
                            if (data.idInvoiceItemGroupGlobal && itemsGroupAlertFlag && ((data.registerPrice !== props.item?.registerPrice) || (data.producerPrice !== props.item?.producerPrice))) {
                                setItemsGroupAlert({ show: true, tabToClose: tabToClose });
                            } else {
                                tabToClose && tabsContext.close(tabToClose);
                            }
                        }
                    },
                    disabled: sendFetch,
                    sendRequestSpinner: sendFetch,
                    tabIndex: getTabIndex('okButton'),
                    'data-field-id': 'okButton'
                }}
            >
                <GridWrapper cols={30} className={styles.gridContainer}>
                    <GridSelectorInput
                        tabIndex={getTabIndex('idGoodsGlobal')}
                        data-field-id="idGoodsGlobal"
                        required
                        label={baseT("name")/*"Наименование"*/}
                        className={styles.field_Goods}
                        selectedEntity={goods}
                        id="idGoodsGlobal"
                        onSelect={(entity) => setGoods(entity)}
                        selectorModalJsx={GoodsSelectorModal}
                        error={errors.idGoodsGlobal}
                        onFocus={() => setErrors({ ...errors, idGoodsGlobal: undefined })}
                        onClear={() => setGoods(undefined)}
                        disabled={props.documentStateProc ? true : false}
                    />
                    <GridSelectorInput
                        tabIndex={getTabIndex('idScalingRatioGlobal')}
                        data-field-id="idScalingRatioGlobal"
                        required
                        label={baseT("unit")/*"Единица измерения"*/}
                        className={styles.field_ScalingRatio}
                        selectedEntity={scalingRatio}
                        id="idScalingRatioGlobal"
                        onSelect={(scalingRatioDto: IEntitySimpleDTO) => {
                            confirmScalingRatioChange(scalingRatioDto, (newData?: IInvoiceItemCreateDTO) => {
                                setData(prevData => ({
                                    ...prevData,
                                    ...newData,
                                    idScalingRatioGlobal: scalingRatioDto.idGlobal
                                }));
                                setScalingRatio(scalingRatioDto);
                            });
                        }}
                        masterIdGlobal={goods?.idGlobal}
                        error={errors.idScalingRatioGlobal}
                        onFocus={() => setErrors({ ...errors, idScalingRatioGlobal: undefined })}
                        selectorModalJsx={ScalingRatioSelectorModal}
                        onClear={() => {
                            setData({ ...data, idScalingRatioGlobal: undefined });
                            setScalingRatio(undefined);
                        }}
                        disabled={props.documentStateProc ? true : false || props.isHardUnProcess}
                    />
                    <div className={classNames(styles.field_Kiz, !isKiz && styles.field_Kiz_Disabled)}> КИЗ </div>
                    <div className={classNames(styles.field_Jv, !isGnvls && styles.field_Jv_Disabled)}> ЖВ </div>
                </GridWrapper>

                <GridWrapper cols={8}>
                    <CurrencyInputWithValidation
                        tabIndex={getTabIndex('countDocs')}
                        data-field-id="countDocs"
                        required
                        error={errors.quantity}
                        keyBan={true}
                        className={styles.field_CountDocs}
                        value={data?.quantity}
                        validation={getCurrencyInputValidation()}
                        min={getKizCount(props.item)}
                        onFocus={() => setErrors({ ...errors, quantity: undefined })}
                        inputId={"countDocs"}
                        label={baseT("quantityDoc")}
                        onChange={(value) => {
                            setData({ ...data, quantity: value });
                            calcActualQuantity(value, 'quantity');
                            getCalculatedInvoiceItem({
                                ...collectionData,
                                eventTypes: "QuantityСhanged",
                                quantity: value,
                            });
                        }}
                        onEndChange={(value) => {
                            onValueChanged(value, pricingInvoice?.quantity, () => {
                                setData({ ...data, quantity: value });
                                getCalculatedInvoiceItemEndChange({
                                    ...collectionData,
                                    eventTypes: "QuantityСhanged",
                                    quantity: value,
                                });
                            });
                        }}
                        disabled={(props.documentStateProc ? true : false) || props.isHardUnProcess}
                    />
                    <NumberInput 
                        className={styles.field_CountFact} 
                        disabled 
                        variant="disabled" 
                        label={baseT("actualQuantity")}/*"Количество факт"*/
                        inputId="actualQuantity" 
                        value={data.actualQuantity}
                        error={errors.actualQuantity}
                    />
                    <NumberInput
                        tabIndex={getTabIndex('damageQuantity')}
                        data-field-id="damageQuantity"
                        className={styles.field_Boi} 
                        label={baseT("damageQuantity")}/*"Бой"*/ 
                        inputId="damageQuantity"
                        value={data.damageQuantity} 
                        onChange={(value) => {
                            calcActualQuantity(value, 'damageQuantity');
                        }} 
                    />
                    <NumberInput
                        tabIndex={getTabIndex('defectQuantity')}
                        data-field-id="defectQuantity"
                        className={styles.field_Defect} 
                        label={baseT("defectQuantity")}/*"Брак"*/ 
                        inputId="defectQuantity" 
                        value={data.defectQuantity} 
                        onChange={(value) => {
                            calcActualQuantity(value, 'defectQuantity');
                        }} 
                    />
                    <NumberInput
                        tabIndex={getTabIndex('shortageQuantity')}
                        data-field-id="shortageQuantity"
                        className={styles.field_Shortage} 
                        label={baseT("shortageQuantity")}/*"Недостача"*/ 
                        inputId="shortageQuantity" 
                        value={data.shortageQuantity} 
                        onChange={(value) => {
                            calcActualQuantity(value, 'shortageQuantity');
                        }}     
                    />
                    <NumberInput className={styles.field_MinCount} disabled variant="disabled" label={baseT("quantityMin")} inputId="minCount" />
                    <div className={styles.field_CheckBox}>
                        <Checkbox
                            tabIndex={getTabIndex('inputKiz')}
                            data-field-id="inputKiz"
                            name="isKiz" 
                            label={t("directory.goods.inputKiz")}//"Ввод КИЗ" 
                            checked={isKiz} 
                            onChange={(checked) => setIsKiz(checked)} 
                            disabled={(props.documentStateProc || props?.displayItem?.countKizScan) ? true : false}
                            tooltip={props?.displayItem?.countKizScan ? baseT("messages.kizScannedNoChange") : null}
                        />
                    </div>
                </GridWrapper>

                <GridWrapper cols={8}>
                    <DecimalInputV2
                        tabIndex={getTabIndex('manufacturerPrice')}
                        data-field-id="manufacturerPrice"
                        required={data.registerPrice ? true : false}
                        label={baseT("manufacturerPrice")}
                        inputId={"manufacturerPrice"}
                        error={errors.producerPrice}
                        onFocus={() => setErrors({ ...errors, producerPrice: undefined })}
                        value={data?.producerPrice}
                        onChange={(value) => {
                            setData({ ...data, producerPrice: value });
                            getCalculatedInvoiceItem({
                                ...collectionData,
                                eventTypes: "ManufacturerPriceChanged",
                                producerPrice: value,
                            });
                        }}
                        onEndChange={(value) => {
                            onValueChanged(value, pricingInvoice?.manufacturerPrice, () => {
                                setData({ ...data, producerPrice: value });
                                getCalculatedInvoiceItemEndChange({
                                    ...collectionData,
                                    eventTypes: "ManufacturerPriceChanged",
                                    producerPrice: value,
                                });
                            });
                        }}
                        disabled={props.documentStateProc ? true : false}
                    />
                    <GridSelectorInput
                        tabIndex={getTabIndex('manufacturerMarginalPrice')}
                        data-field-id="manufacturerMarginalPrice"
                        numereticInput
                        label={baseT("manufacturerMarginalPrice")}
                        id="manufacturerMarginalPrice" 
                        data={{...goods, validateReestr}}
                        selectedEntity={data.registerPrice}
                        selectorModalJsx={RegisterPriceSelectorModal}
                        onSelect={(entity) => {
                            setData({ ...data, registerPrice: entity});
                            if(data?.producerPrice > entity ) {
                                setErrors({...errors, producerPrice: errorsT('exceededProducerPrice')});
                            }
                        }}
                        onClear={() => setData({ ...data, registerPrice: 0 })}
                        disabled={props.documentStateProc || (validateReestr.isGnvls === false) ? true : false}
                        error={errors.registerPrice}
                        onFocus={() => setErrors({ ...errors, registerPrice: undefined })}
                    />
                        <GridSelectorInput
                            id="idStorePlace"
                            label={baseT('storePlace')}
                            selectorModalJsx={StorePlaceSelectorModal}
                            selectedEntity={storePlace}
                            onSelect={(entity) => {
                                setData({ ...data, idStorePlaceGlobal: entity.idGlobal })
                                setStorePlace(entity)
                            } }
                            onClear={() => {
                                 setData({ ...data, idStorePlaceGlobal: undefined })
                                 setStorePlace(undefined)
                            }}
                        />
                    <TextInput className={styles.field_Gtd} disabled label={baseT("gtd")} inputId={"gtd"} />
                    <NumberInput className={styles.field_AdditionalShK} disabled label={baseT("addBarcode")} inputId={"additionalShK"} />
                </GridWrapper>

                <Accordion opened={true} caption={baseT("calcSellingPrice")} id="CalculationOfTheSellingPriceInvoiceItem">
                    <FlexRow wrap>
                        <GridSelectorInput
                            tabIndex={getTabIndex('vatRateWholesale')}
                            data-field-id="vatRateWholesale"
                            required
                            selectedEntity={vatRateWholesale}
                            label={baseT("vatRateWholesale")}
                            id="idGoodsGlobal"
                            disabled={((!props.currentSupplier?.useVat && props.variant === 'create') || props.documentStateProc) ? true : false}
                            selectorModalJsx={TaxTypeSelectorModal}
                            onSelect={(entity) => {
                                setVatRateWholesale(entity);
                                let vatValue = 0;
                                if (entity?.idGlobal) {
                                    taxTypeDataProvider.getById(entity.idGlobal as string, (t) => {
                                        vatValue = t.taxRate;
                                        setData({ ...data, supplierCostInfo: { ...data?.supplierCostInfo, vat: vatValue } });
                                        calcBySupplierVatRatePercentageChanged(vatValue);
                                    });
                                } else {
                                    setData({ ...data, supplierCostInfo: { ...data?.supplierCostInfo, vat: vatValue } });
                                    calcBySupplierVatRatePercentageChanged(vatValue);
                                }
                            }}
                            onClear={() => {
                                setVatRateWholesale(undefined);
                            }}
                            error={errors.supplierVat}
                            onFocus={() => setErrors({ ...errors, supplierVat: undefined })}
                        />
                        <DecimalInputV2 className={styles.field_WholesaleSurcharge} disabled label={baseT("supplierMarkupPercentage")} inputId={"SupplierMarkupPercentageChanged"} value={data?.supplierCostInfo?.adprice} />
                        <DecimalInputV2 disabled label={baseT("supplierVatPerUnit")} inputId={"SupplierVatPerUnitChanged"} value={data?.supplierCostInfo?.vatPrice} />

                        <DecimalInputV2
                            tabIndex={getTabIndex('suppliersPrice')}
                            data-field-id="suppliersPrice"
                            required
                            label={baseT("suppliersPrice")}
                            inputId={"suppliersPrice"}
                            value={data?.supplierCostInfo?.price}
                            error={errors.supplierPrice}
                            onFocus={() => setErrors({ ...errors, supplierPrice: undefined })}
                            onChange={(value) => {
                                setData({ ...data, supplierCostInfo: { ...data?.supplierCostInfo, price: value } });
                                getCalculatedInvoiceItem({
                                    ...collectionData,
                                    eventTypes: "SupplierPriceExcludingVatChanged",
                                    supplierCostInfo: { ...data?.supplierCostInfo, price: value },
                                });
                            }}
                            onEndChange={(value) => {
                                onValueChanged(value, pricingInvoice?.supplierPriceExcludingVat, () => {
                                    setData({ ...data, supplierCostInfo: { ...data?.supplierCostInfo, price: value } });
                                    getCalculatedInvoiceItemEndChange({
                                        ...collectionData,
                                        eventTypes: "SupplierPriceExcludingVatChanged",
                                        supplierCostInfo: { ...data?.supplierCostInfo, price: value },
                                    });
                                })
                            }}
                            disabled={props.documentStateProc ? true : false}
                        />
                        <DecimalInputV2
                            tabIndex={getTabIndex('supplierAmount')}
                            data-field-id="supplierAmount"
                            required
                            label={baseT("supplierAmount")}
                            inputId={"supplierAmount"}
                            value={data?.supplierCostInfo?.sum}
                            error={errors.supplierSum}
                            onFocus={() => setErrors({ ...errors, supplierSum: undefined })}
                            onChange={(value) => {
                                setData({ ...data, supplierCostInfo: { ...data?.supplierCostInfo, sum: value } });
                                getCalculatedInvoiceItem({
                                    ...collectionData,
                                    eventTypes: "SupplierAmountExcludingVatChanged",
                                    supplierCostInfo: { ...data?.supplierCostInfo, sum: value },
                                });
                            }}
                            onEndChange={(value) => {
                                onValueChanged(value, pricingInvoice?.supplierAmountExcludingVat, () => {
                                    setData({ ...data, supplierCostInfo: { ...data?.supplierCostInfo, sum: value } });
                                    getCalculatedInvoiceItemEndChange({
                                        ...collectionData,
                                        eventTypes: "SupplierAmountExcludingVatChanged",
                                        supplierCostInfo: { ...data?.supplierCostInfo, sum: value },
                                    });
                                })
                            }}
                            disabled={props.documentStateProc ? true : false}
                        />
                        <DecimalInputV2
                            tabIndex={getTabIndex('supplierVatAmount')}
                            data-field-id="supplierVatAmount"
                            label={baseT("supplierVatAmount")}
                            inputId={"supplierVatAmount"}
                            value={data?.supplierCostInfo?.vatSum}
                            error={errors.supplierVatSum}
                            onFocus={() => setErrors({ ...errors, supplierVatSum: undefined })}
                            onChange={(value) => {
                                setData({ ...data, supplierCostInfo: { ...data?.supplierCostInfo, vatSum: value } });
                                getCalculatedInvoiceItem({
                                    ...collectionData,
                                    eventTypes: "SupplierVatAmountChanged",
                                    supplierCostInfo: { ...data?.supplierCostInfo, vatSum: value },
                                });
                            }}
                            onEndChange={(value) => {
                                onValueChanged(value, pricingInvoice?.supplierVatAmount, () => {
                                    setData({ ...data, supplierCostInfo: { ...data?.supplierCostInfo, vatSum: value } });
                                    getCalculatedInvoiceItemEndChange({
                                        ...collectionData,
                                        eventTypes: "SupplierVatAmountChanged",
                                        supplierCostInfo: { ...data?.supplierCostInfo, vatSum: value },
                                    });
                                })
                            }}
                            disabled={props.documentStateProc ? true : false}
                        />
                        <DecimalInputV2
                            tabIndex={getTabIndex('supplierPriceWithVat')}
                            data-field-id="supplierPriceWithVat"
                            required
                            label={baseT("supplierPriceWithVat")}
                            inputId={"supplierPriceWithVat"}
                            value={data?.supplierCostInfo?.priceIncVat}
                            error={errors.supplierPriceWithVat}
                            onFocus={() => setErrors({ ...errors, supplierPriceWithVat: undefined })}
                            onChange={(value) => {
                                setData({ ...data, supplierCostInfo: { ...data?.supplierCostInfo, priceIncVat: value } });
                                getCalculatedInvoiceItem({
                                    ...collectionData,
                                    eventTypes: "SupplierPriceWithVatChanged",
                                    supplierCostInfo: { ...data?.supplierCostInfo, priceIncVat: value },
                                });
                            }}
                            onEndChange={(value) => {
                                onValueChanged(value, pricingInvoice?.supplierPriceWithVat, () => {
                                    setData({ ...data, supplierCostInfo: { ...data?.supplierCostInfo, priceIncVat: value } });
                                    getCalculatedInvoiceItemEndChange({
                                        ...collectionData,
                                        eventTypes: "SupplierPriceWithVatChanged",
                                        supplierCostInfo: { ...data?.supplierCostInfo, priceIncVat: value },
                                    });
                                })
                            }}
                            disabled={props.documentStateProc ? true : false}
                        />
                        <DecimalInputV2
                            tabIndex={getTabIndex('amountOfThePaymentWithVat')}
                            data-field-id="amountOfThePaymentWithVat"
                            required
                            label={baseT("amountOfThePaymentWithVat")}
                            inputId={"amountOfThePaymentWithVat"}
                            value={data?.supplierCostInfo?.sumIncVat}
                            error={errors.supplierSumWithVat}
                            onFocus={() => setErrors({ ...errors, supplierSumWithVat: undefined })}
                            onChange={(value) => {
                                setData({ ...data, supplierCostInfo: { ...data?.supplierCostInfo, sumIncVat: value } });
                                getCalculatedInvoiceItem({
                                    ...collectionData,
                                    eventTypes: "SupplierAmountWithVatChanged",
                                    supplierCostInfo: { ...data?.supplierCostInfo, sumIncVat: value },
                                });
                            }}
                            onEndChange={(value) => {
                                onValueChanged(value, pricingInvoice?.supplierAmountWithVat, () => {
                                    setData({ ...data, supplierCostInfo: { ...data?.supplierCostInfo, sumIncVat: value } });
                                    getCalculatedInvoiceItemEndChange({
                                        ...collectionData,
                                        eventTypes: "SupplierAmountWithVatChanged",
                                        supplierCostInfo: { ...data?.supplierCostInfo, sumIncVat: value },
                                    });
                                })
                            }}
                            disabled={props.documentStateProc ? true : false}
                        />
                    </FlexRow>

                    <FlexRow wrap>
                        <GridSelectorInput
                            tabIndex={getTabIndex('retailVatRate')}
                            data-field-id="retailVatRate"
                            required
                            selectedEntity={retailVatRate}
                            label={baseT("retailVatRate")}
                            id="idGoodsGlobal"
                            selectorModalJsx={TaxTypeSelectorModal}
                            onSelect={(entity) => {
                                setRetailVatRate(entity);
                                let vatValue = 0;
                                if (entity?.idGlobal) {
                                    taxTypeDataProvider.getById(entity.idGlobal, (t) => {
                                        vatValue = t.taxRate;
                                        setData({ ...data, retailCostInfo: { ...data?.retailCostInfo, vat: vatValue } });
                                        calcByPercentageOfVatRateRetailChanged(vatValue);
                                    });
                                } else {
                                    setData({ ...data, retailCostInfo: { ...data?.retailCostInfo, vat: vatValue } });
                                    calcByPercentageOfVatRateRetailChanged(vatValue);
                                }
                            }}
                            onClear={() => setRetailVatRate(undefined)}
                            error={errors.retailVat}
                            onFocus={() => setErrors({ ...errors, retailVat: undefined })}
                            disabled={props.documentStateProc ? true : false}
                        />
                        <DecimalInputV2
                            tabIndex={getTabIndex('percentageOfMarkupRetailChanged')}
                            data-field-id="percentageOfMarkupRetailChanged"
                            label={baseT("adprice")}
                            inputId={"PercentageOfMarkupRetailChanged"}
                            value={data.retailCostInfo.adprice}
                            onChange={(value) => {
                                setData({ ...data, retailCostInfo: { ...data?.retailCostInfo, adprice: value } });
                                getCalculatedInvoiceItem({
                                    ...collectionData,
                                    eventTypes: "PercentageOfMarkupRetailChanged",
                                    retailCostInfo: { ...data?.retailCostInfo, adprice: value },
                                });
                            }}
                            onEndChange={(value) => {
                                onValueChanged(value, pricingInvoice?.percentageOfMarkupRetail, () => {
                                    setData({ ...data, retailCostInfo: { ...data?.retailCostInfo, adprice: value } });
                                    getCalculatedInvoiceItemEndChange({
                                        ...collectionData,
                                        eventTypes: "PercentageOfMarkupRetailChanged",
                                        retailCostInfo: { ...data?.retailCostInfo, adprice: value },
                                    });
                                });
                            }}
                            disabled={props.documentStateProc ? true : false}
                        />
                        <DecimalInputV2 disabled label={baseT("vatPrice")} inputId={"interestMargin"} value={data.retailCostInfo.vatPrice} />
                        <DecimalInputV2
                            tabIndex={getTabIndex('retailPrice')}
                            data-field-id="retailPrice"
                            required
                            label="Цена б/НДС, розн."
                            inputId={"retailPrice"}
                            value={data?.retailCostInfo?.price}
                            error={errors.retailPrice}
                            onFocus={() => setErrors({ ...errors, retailPrice: undefined })}
                            onChange={(value) => {
                                setData({ ...data, retailCostInfo: { ...data?.retailCostInfo, price: value } });
                                getCalculatedInvoiceItem({
                                    ...collectionData,
                                    eventTypes: "RetailPriceChanged",
                                    retailCostInfo: { ...data?.retailCostInfo, price: value },
                                });
                            }}
                            onEndChange={(value) => {
                                onValueChanged(value, pricingInvoice?.retailPrice, () => {
                                    setData({ ...data, retailCostInfo: { ...data?.retailCostInfo, price: value } });
                                    getCalculatedInvoiceItemEndChange({
                                        ...collectionData,
                                        eventTypes: "RetailPriceChanged",
                                        retailCostInfo: { ...data?.retailCostInfo, price: value },
                                    });
                                })
                            }}
                            disabled={props.documentStateProc ? true : false}
                        />
                        <DecimalInputV2
                            tabIndex={getTabIndex('retailAmountWithoutVat')}
                            data-field-id="retailAmountWithoutVat"
                            required
                            label={baseT("retailAmountWithoutVat")} // "Сумма розн. б/НДС"
                            inputId={"retailAmountWithoutVat"}
                            value={data?.retailCostInfo?.sum}
                            error={errors.retailSum}
                            onFocus={() => setErrors({ ...errors, retailSum: undefined })}
                            onChange={(value) => {
                                setData({ ...data, retailCostInfo: { ...data?.retailCostInfo, sum: value } });
                                getCalculatedInvoiceItem({
                                    ...collectionData,
                                    eventTypes: "RetailAmountChanged",
                                    retailCostInfo: { ...data?.retailCostInfo, sum: value },
                                });
                            }}
                            onEndChange={(value) => {
                                onValueChanged(value, pricingInvoice?.retailAmount, () => {
                                    setData({ ...data, retailCostInfo: { ...data?.retailCostInfo, sum: value } });
                                    getCalculatedInvoiceItemEndChange({
                                        ...collectionData,
                                        eventTypes: "RetailAmountChanged",
                                        retailCostInfo: { ...data?.retailCostInfo, sum: value },
                                    });
                                })
                            }}
                            disabled={props.documentStateProc ? true : false}
                        />
                        <DecimalInputV2
                            tabIndex={getTabIndex('retailAmountWithVatSum')}
                            data-field-id="retailAmountWithVatSum"
                            label={baseT("retailVatAmount")}//"Сумма НДС, розн."
                            inputId={"retailAmountWithVatSum"}
                            value={data?.retailCostInfo?.vatSum}
                            error={errors.retailVatSum}
                            onFocus={() => setErrors({ ...errors, retailVatSum: undefined })}
                            onChange={(value) => {
                                setData({ ...data, retailCostInfo: { ...data?.retailCostInfo, vatSum: value } });
                                getCalculatedInvoiceItem({
                                    ...collectionData,
                                    eventTypes: "VatAmountRetailChanged",
                                    retailCostInfo: { ...data?.retailCostInfo, vatSum: value },
                                });
                            }}
                            onEndChange={(value) => {
                                onValueChanged(value, pricingInvoice?.vatAmountRetail, () => {
                                    setData({ ...data, retailCostInfo: { ...data?.retailCostInfo, vatSum: value } });
                                    getCalculatedInvoiceItemEndChange({
                                        ...collectionData,
                                        eventTypes: "VatAmountRetailChanged",
                                        retailCostInfo: { ...data?.retailCostInfo, vatSum: value },
                                    });
                                })
                            }}
                            disabled={props.documentStateProc ? true : false}
                        />
                        <DecimalInputV2
                            tabIndex={getTabIndex('sellingPrice')}
                            data-field-id="sellingPrice"
                            required
                            variant="green"
                            labelVariant="green"
                            className={styles.boldLabel}
                            label={baseT("sellingPrice")}
                            inputId={"sellingPrice"}
                            value={data?.retailCostInfo?.priceIncVat ?? 0}
                            disabled={(props.gosContractFields.referentPrice == false || props.documentStateProc || disabledGosContract) ? true : false}
                            error={errors.retailPriceWithVat}
                            onFocus={() => setErrors({ ...errors, retailPriceWithVat: undefined })}
                            onChange={(value) => {
                                setData({ ...data, retailCostInfo: { ...data?.retailCostInfo, priceIncVat: value } });
                                getCalculatedInvoiceItem({
                                    ...collectionData,
                                    eventTypes: "RetailPriceWithVatChanged",
                                    retailCostInfo: { ...data?.retailCostInfo, priceIncVat: value },
                                });
                            }}
                            onEndChange={(value) => {
                                onValueChanged(value, pricingInvoice?.retailPriceWithVat, () => {
                                    setData({ ...data, retailCostInfo: { ...data?.retailCostInfo, priceIncVat: value } });
                                    getCalculatedInvoiceItemEndChange({
                                        ...collectionData,
                                        eventTypes: "RetailPriceWithVatChanged",
                                        retailCostInfo: { ...data?.retailCostInfo, priceIncVat: value },
                                    });
                                })
                            }}
                        />
                        <DecimalInputV2
                            tabIndex={getTabIndex('retailAmountWithVat')}
                            data-field-id="retailAmountWithVat"
                            required
                            variant="green"
                            labelVariant="green"
                            className={styles.boldLabel}
                            label={baseT("retailAmountWithVat")}
                            inputId={"retailAmountWithVat"}
                            value={+data?.retailCostInfo?.sumIncVat.toFixed(2)}
                            error={errors.retailSumWithVat}
                            onFocus={() => setErrors({ ...errors, retailSumWithVat: undefined })}
                            onChange={(value) => {
                                setData({ ...data, retailCostInfo: { ...data?.retailCostInfo, sumIncVat: value } });
                                getCalculatedInvoiceItem({
                                    ...collectionData,
                                    eventTypes: "RetailAmountWithVatChanged",
                                    retailCostInfo: { ...data?.retailCostInfo, sumIncVat: value },
                                });
                            }}
                            onEndChange={(value) => {
                                onValueChanged(value, pricingInvoice?.retailAmountWithVat, () => {
                                    setData({ ...data, retailCostInfo: { ...data?.retailCostInfo, sumIncVat: value } });
                                    getCalculatedInvoiceItemEndChange({
                                        ...collectionData,
                                        eventTypes: "RetailAmountWithVatChanged",
                                        retailCostInfo: { ...data?.retailCostInfo, sumIncVat: value },
                                    });
                                })
                            }}
                            disabled={props.documentStateProc || disabledGosContract ? true : false}
                        />
                        {   props.gosContractFields.referentPrice &&
                            <DecimalInputV2
                                tabIndex={getTabIndex('priceContract')}
                                data-field-id="priceContract"
                                required
                                variant="blue"
                                labelVariant="blue"
                                className={styles.boldLabel}
                                label={baseT("priceContract")}
                                inputId={"priceContract"}
                                value={priceContract}
                                disabled={true}
                            />
                        }
                    
                    </FlexRow>
                    <GridWrapper cols={8}>
                        <span className={styles.link_sertificate} onClick={() => setIsOpenSeriesModal(true)}>
                            {t("documents.invoice.certsAndSeries")/*Серии и сертификаты F9*/}
                        </span>
                        <span className={styles.link}>{t("documents.invoice.regCerts")}</span>
                        <span className={styles.link}>{t("documents.invoice.historyDelivery")}</span>
                    </GridWrapper>
                </Accordion>

                {itemsGroupAlert.show && (
                    <MessageModalWindow
                        message={baseT("messages.kizLotPriceChanged")}
                        ok={{
                            onClick: () => {
                                setItemsGroupAlert({ show: false });
                                itemsGroupAlert.tabToClose && tabsContext.close(itemsGroupAlert.tabToClose);
                            },
                        }}
                        cancel={{ onClick: () => {}, hidden: true }}
                    />
                )}

                {(isOpenSeriesModal && !props.documentStateProc) && (
                    <SeriesModalWindow 
                        cancel={() => setIsOpenSeriesModal(false)} 
                        goods={goods as IEntitySimpleDTO} 
                        setItemSeries={(value: ISerialDTO) => setItemSeries(value)} 
                        itemSeries={itemSeries} 
                    />
                )}
                {modalWindow}
            </BaseDocumentCreator>
        </div>
    );
};

export default InvoiceItemCreator;
